// @ts-nocheck — slated for deletion
/* eslint-disable */
import React, { useCallback, useMemo } from "react"

import { useActiveOrganization } from "@/contexts"
import { useAvailableReportDates } from "@/services"
import { APP_COMPANY_NAME } from "@/utils/constants"
import { getMostRecentPreviousYearDateRange } from "@/utils/date/date"
import { useDateRange } from "@/utils/date/use-date-range"
import type { Interval } from "luxon"

import { WarningAmberRounded } from "@mui/icons-material"
import { Alert, Box, FormControl, FormLabel } from "@mui/material"

import { MonthRangeSelector } from "../../../../components/date/monthRangeSelector/monthRangeSelector"
import type {
  GresbEnergyReportTableRow,
  GresbGhgReportTableRow,
  GresbWaterReportTableRow,
} from "../../models/gresb"
import type { Report } from "../../models/report"
import { reportCard } from "../../models/report"
import { useGresbReport } from "../../services/useGresbReport/useGresbReport"
import DataAlert from "../dataAlert/dataAlert"
import DataSummary from "../dataSummary/dataSummary"
import DownloadPreviewCsv from "../downloadPreviewCsv/downloadPreviewCsv"
import ReportLayout from "../reportLayout/reportLayout"

/**
 * A component used to build a GRESB report
 *
 * @example
 * return (
 *   <Gresb />
 * )
 */
const Gresb = () => {
  const { activeOrganization } = useActiveOrganization()
  const { availableReportDatesData } = useAvailableReportDates()

  const mostRecentPreviousYearDateRange = getMostRecentPreviousYearDateRange(
    availableReportDatesData
  )

  const { dateRange, setDateRange } = useDateRange({
    id: "reportGresbDateRange",
    bounds: availableReportDatesData,
    fallbackDateRange: mostRecentPreviousYearDateRange,
  })

  const isDataReady = false

  const { gresbReport, isGresbReportLoading } = useGresbReport(
    activeOrganization?.id,
    dateRange,
    isDataReady
  )

  // Mass lint disable

  const reports: Report<
    | GresbEnergyReportTableRow
    | GresbGhgReportTableRow
    | GresbWaterReportTableRow
  >[] = useMemo(
    // Mass eslint disable @typescript-eslint/no-unsafe-return

    () => (gresbReport ? Object.values(gresbReport) : []),
    [gresbReport]
  )

  const handleDateRangeChange = useCallback(
    (value: Interval | null): void => {
      if (value?.isValid) {
        setDateRange(value)
      }
    },
    [setDateRange]
  )

  return (
    <ReportLayout
      actions={
        <DownloadPreviewCsv
          isDisabled={!gresbReport || isGresbReportLoading}
          isLoading={isGresbReportLoading}
          report={reports}
        />
      }
      details={
        <DataSummary>
          <Box component="p" sx={{ mt: 0, mb: 2 }}>
            Your data download includes 3 individual CSV files for energy,
            emissions, and water sections of a GRESB report
          </Box>
          <Box component="p" sx={{ m: 0 }}>
            Your emissions CSV will include two data points for scope 2
            emissions:
          </Box>
          <Box component="ol" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
            <li>Location based emissions (eGRID)</li>
            <li>{`${APP_COMPANY_NAME} advanced grid study`}</li>
          </Box>
          <Alert icon={<WarningAmberRounded />} severity="warning">
            This download does not include market-based emissions (optional for
            GRESB). Contact us to explore adding market-based emissions.
          </Alert>
        </DataSummary>
      }
      filters={
        <FormControl>
          <FormLabel sx={{ mb: 0.5 }}>Date Range</FormLabel>
          <MonthRangeSelector
            availableMaxMinMonths={availableReportDatesData}
            onChange={handleDateRangeChange}
            value={dateRange}
          />
        </FormControl>
      }
      notifications={<DataAlert />}
      report={reportCard.Gresb}
    />
  )
}

export default Gresb
